import type { DealRow, InvoiceRow, PeriodRow } from "../data/queries";
import type { InvoiceView, Tone } from "./invoice-view";
import { date } from "../lib/format";
import { t } from "../lib/sprache";

/** „Was heute zu tun ist“: offene Aufgaben der Agentur aus Monaten und Rechnungen, nach Frist sortiert. */
export interface Todo {
  key: string;
  to: string;
  title: string;
  detail: string;
  due: string | null;
  tone: Tone;
}

export interface TodoInput {
  today: string;
  deals: readonly DealRow[];
  periods: readonly PeriodRow[];
  invoices: readonly InvoiceView[];
  talentName: (id: string | null) => string;
  brandName: (id: string | null) => string;
  can: { dun: boolean; send: boolean; approve: boolean; confirm: boolean; payout?: boolean };
}

const ACTIVE_DEAL = (d: DealRow) => !d.archived_at && d.status !== "cancelled" && d.status !== "done";

export function agencyTodos(input: TodoInput): Todo[] {
  const deals = new Map(input.deals.map((d) => [d.id, d]));
  const out: Todo[] = [];
  const dealOf = (row: InvoiceRow) => (row.deal_id ? deals.get(row.deal_id) : undefined);

  for (const p of input.periods) {
    const d = deals.get(p.deal_id);
    if (!d || !ACTIVE_DEAL(d)) continue;
    if (p.manual_status === "content_approval") {
      out.push({ key: `content:${p.id}`, to: `/agentur/kooperationen/${d.id}?monat=${p.month.slice(0, 7)}`, title: t("Content prüfen", "Review content"),
        detail: `${input.talentName(d.talent_id)} · ${input.brandName(d.brand_id)}`, due: p.content_deadline, tone: "warning" });
    }
  }
  for (const v of input.invoices) {
    const r = v.row;
    const d = dealOf(r);
    const who = input.talentName(r.talent_id);
    const brand = input.brandName(r.brand_id ?? d?.brand_id ?? null);
    const to = `/agentur/rechnungen/${r.id}`;
    const no = r.number ?? "";
    // Ohne das nötige Recht wird die Aufgabe nicht verschwiegen, sondern als Hinweis an die Berechtigten geführt
    const push = (allowed: boolean, tl: Omit<Todo, "detail"> & { detail: string }) =>
      out.push(allowed ? tl : { ...tl, title: t(`${tl.title} (Admin informieren)`, `${tl.title} (inform the admin)`).replace("  ", " "), tone: "info" });
    switch (v.state.state) {
      case "in_review":
        push(input.can.approve, { key: `review:${r.id}`, to, title: t(`Rechnung ${no} prüfen`, `Review invoice ${no}`).replace("  ", " "), detail: `${who} · ${brand}`, due: r.effective_due_date, tone: "warning" });
        break;
      case "approved":
      case "send_overdue": {
        const tone: Tone = v.state.state === "send_overdue" ? "error" : "warning";
        const credit = !!v.record.creditFor;
        if (r.kind === "talent_to_agency") {
          push(!!input.can.payout, { key: `run:${r.id}`, to, title: t(`Rechnung ${no} in den Zahlungslauf geben`, `Put invoice ${no} into the payment run`), detail: `${who} · ${brand}`, due: r.issue_date, tone });
        } else if (r.kind === "agency_to_talent") {
          push(input.can.send, { key: `send:${r.id}`, to, title: t(`${credit ? t("Stornorechnung", "Cancellation invoice") : t("Rechnung", "Invoice")} ${no} an den Creator senden`, `Send ${credit ? t("Stornorechnung", "Cancellation invoice") : t("Rechnung", "Invoice")} ${no} to the creator`), detail: who, due: r.issue_date, tone });
        } else {
          push(input.can.send, { key: `send:${r.id}`, to, title: t(`${credit ? t("Stornorechnung", "Cancellation invoice") : t("Rechnung", "Invoice")} ${no} an Marke senden`, `Send ${credit ? t("Stornorechnung", "Cancellation invoice") : t("Rechnung", "Invoice")} ${no} to brand`), detail: `${brand} · ${who}`, due: r.issue_date, tone });
        }
        break;
      }
      case "overdue":
      case "dunning":
      case "due_today": {
        const next = v.next?.action ?? null;
        // CO-003 (Praxistest 29.10.2026): Läuft eine verlängerte Zahlungsfrist noch, ist keine Mahnung fällig (wie die Mahnseite)
        if (next === "send_dunning" && !(r.effective_due_date && r.effective_due_date >= input.today)) {
          push(input.can.dun, { key: `dun:${r.id}`, to: `${to}/erinnern`, title: t(`Überfällige Rechnung ${no} erinnern`, `Remind about overdue invoice ${no}`), detail: t(`${brand} · seit ${v.state.daysOverdue} Tagen`, `${brand} · for ${v.state.daysOverdue} days`), due: r.effective_due_date, tone: "error" });
        } else if (next === "request_payment_check") {
          out.push({ key: `check:${r.id}`, to, title: t(`Beim Creator nach Zahlung zu ${no || t("Rechnung", "Invoice")} fragen`, `Ask the creator about payment for ${no || t("Rechnung", "Invoice")}`), detail: `${who} · ${brand}`, due: r.effective_due_date, tone: "warning" });
        } else if (next === "confirm_payout_and_ask") {
          push(!!input.can.payout, { key: `payout:${r.id}`, to, title: t(`Auszahlung zu ${no || t("Rechnung", "Invoice")} anweisen`, `Order payout for ${no || t("Rechnung", "Invoice")}`), detail: `${who} · ${brand}`, due: r.effective_due_date, tone: "warning" });
        } else if (next === "hand_over_to_collection") {
          push(input.can.dun, { key: `coll:${r.id}`, to, title: t(`Rechnung ${no}: Inkasso prüfen`, `Invoice ${no}: check debt collection`), detail: t(`${brand} · letzte Mahnfrist abgelaufen`, `${brand} · last dunning deadline passed`), due: r.effective_due_date, tone: "error" });
        }
        break;
      }
      case "payment_reported":
        push(input.can.confirm, { key: `pay:${r.id}`, to, title: t(`Zahlung zu ${no || t("Rechnung", "Invoice")} bestätigen`, `Confirm payment for ${no || t("Rechnung", "Invoice")}`), detail: `${who} · ${brand}`, due: null, tone: "info" });
        break;
      case "money_missing":
        push(input.can.confirm, { key: `missing:${r.id}`, to, title: t(`Zahlung zu ${no || t("Rechnung", "Invoice")} fehlt laut Creator`, `Payment for ${no || t("Rechnung", "Invoice")} missing according to creator`), detail: t(`${who} · ${brand} · bitte klären`, `${who} · ${brand} · please clarify`), due: r.effective_due_date, tone: "error" });
        break;
      case "cancelled_refund_pending":
        // Rechnung des Creators, schon ausgezahlt: Rückforderung beim Creator, keine Erstattung an die Marke (Nachtest K-157)
        if (r.kind === "talent_to_agency") push(input.can.confirm, { key: `refund:${r.id}`, to, title: t(`Rückforderung zu ${no || t("Rechnung", "Invoice")} klären`, `Clarify repayment claim for ${no || t("Rechnung", "Invoice")}`), detail: t(`${who} · storniert, aber schon ausgezahlt`, `${who} · cancelled but already paid out`), due: null, tone: "warning" });
        else push(input.can.confirm, { key: `refund:${r.id}`, to, title: t(`Erstattung zu ${no || t("Rechnung", "Invoice")} klären`, `Clarify refund for ${no || t("Rechnung", "Invoice")}`), detail: t(`${brand} · storniert, aber schon bezahlt`, `${brand} · cancelled but already paid`), due: null, tone: "warning" });
        break;
    }
  }
  const talentPeriods = new Set(input.invoices.filter((v) => v.row.kind !== "agency_to_brand" && !["draft", "cancelled", "withdrawn", "discarded", "rejected"].includes(v.row.status)).map((v) => v.row.period_id));
  for (const p of input.periods) {
    const d = deals.get(p.deal_id);
    if (!d || !ACTIVE_DEAL(d) || p.status === "cancelled" || !p.invoice_requested_at || talentPeriods.has(p.id)) continue;
    out.push({ key: `request:${p.id}`, to: `/agentur/kooperationen/${d.id}?monat=${p.month.slice(0, 7)}`, title: t("Rechnung des Creators ausstehend", "Creator invoice pending"),
      detail: `${input.talentName(d.talent_id)} · ${input.brandName(d.brand_id)}`, due: p.content_deadline, tone: "warning" });
  }
  const rank: Record<Tone, number> = { error: 0, warning: 1, info: 2, neutral: 3, success: 4 };
  return out.sort((a, b) => (a.due ?? "9999").localeCompare(b.due ?? "9999") || rank[a.tone] - rank[b.tone]);
}

/** „Heute“, „Morgen“, „Überfällig“ oder Datum – für die Pille neben einer Aufgabe. */
export function dueLabel(due: string | null, today: string): string {
  if (!due) return t("Offen", "Open");
  if (due < today) return t("Überfällig", "Overdue");
  if (due === today) return t("Heute", "Today");
  const morgen = new Date(`${today}T00:00:00Z`);
  morgen.setUTCDate(morgen.getUTCDate() + 1);
  if (due === morgen.toISOString().slice(0, 10)) return t("Morgen", "Tomorrow");
  return date(due);
}
